import { Component, ChangeDetectionStrategy } from '@angular/core';
import { LikesService } from '../../../likes/likes.service';
import { ModelService } from '../../../product/model.service';
import { MediaService } from '../../../media/media.service';
import { Like } from '../../../models/like.model';
import { BehaviorSubject, combineLatest, Observable, of, forkJoin } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';

export interface UnifiedDisplayItem {
  id: string;
  type: 'model' | 'image' | 'video';
  likedAt: Date;
  data: any; // Model | Image | Video
}

@Component({
  selector: 'app-my-likes',
  templateUrl: './my-likes.component.html',
  styleUrls: ['./my-likes.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyLikesComponent {
  private filterSubject = new BehaviorSubject<'all' | 'model' | 'image' | 'video'>('all');
  private sortOrderSubject = new BehaviorSubject<'asc' | 'desc'>('desc');

  filter$ = this.filterSubject.asObservable();
  sortOrder$ = this.sortOrderSubject.asObservable();

  likes$ = this.likesService.getMyLikes$();

  displayItems$: Observable<UnifiedDisplayItem[]> = this.likes$.pipe(
    switchMap((likes: Like[]) => {
      if (!likes || likes.length === 0) return of([]);
      return forkJoin(
        likes.map(like => {
          let data$!: Observable<any>;
          if (like.targetType === 'model') {
            data$ = this.modelService.getModelById(like.targetId);
          } else if (like.targetType === 'image') {
            data$ = this.mediaService.getImageById(like.targetId);
          } else {
            data$ = this.mediaService.getVideoById(like.targetId);
          }
          return data$.pipe(
            map(data => ({
              id: like.targetId,
              type: like.targetType,
              likedAt: like.createdAt?.toDate ? like.createdAt.toDate() : new Date(like.createdAt),
              data
            }) as UnifiedDisplayItem),
            catchError(err => {
              console.warn('Failed to load liked item', like, err);
              return of(null);
            })
          );
        })
      ).pipe(map(items => items.filter(Boolean) as UnifiedDisplayItem[]));
    })
  );

  filteredAndSortedItems$: Observable<UnifiedDisplayItem[]> = combineLatest([
    this.displayItems$,
    this.filter$,
    this.sortOrder$
  ]).pipe(
    map(([items, filter, sort]) => {
      let filtered = items;
      if (filter !== 'all') {
        filtered = filtered.filter(i => i.type === filter);
      }
      const sorted = filtered.sort((a, b) => {
        const diff = a.likedAt.getTime() - b.likedAt.getTime();
        return sort === 'asc' ? diff : -diff;
      });
      return [...sorted];
    })
  );

  constructor(
    private likesService: LikesService,
    private modelService: ModelService,
    private mediaService: MediaService
  ) {}

  setFilter(filter: 'all' | 'model' | 'image' | 'video') {
    this.filterSubject.next(filter);
  }

  toggleSortOrder() {
    this.sortOrderSubject.next(this.sortOrderSubject.value === 'asc' ? 'desc' : 'asc');
  }
}
